<template>
  <SloModal :router="!this.global" :maxWidth="850" class="add-to-project">
    <template v-slot:title>
      {{ 'Add a' | t }} {{ type }} {{ 'to DesignSpec' | t }}
    </template>
    <div class="content">
      <section class="product-thumbnail">
        <figure
          v-if="
            thumbnail &&
            (thumbnail.indexOf('/') === 0 || thumbnail.indexOf('http') === 0)
          "
        >
          <img :src="thumbnail" :alt="'Sloan ' + type" width="385" height="380" />
        </figure>
        <div v-else v-html="thumbnail" />
      </section>
      <section v-stack-y:md>
        <SloSelect
          v-model="project"
          :placeholder="'Select a project' | t"
          @input="handleProjectSelection"
        >
          <option
            v-if="projects.length > 1"
            v-for="projectOption of projects"
            :key="projectOption.id"
            :value="projectOption.id"
            v-html="projectOption.name"
          />
          <option
            v-else
            v-html="projects[0].name"
            :value="projects[0].id"
            selected
          />
        </SloSelect>
        <SloCheckList
          v-if="!loading && sections && sections.length > 0"
          v-model="addToSections"
          :items="checklistItems"
        >
          <SloLabel>{{ 'Add to section(s)' | t }}</SloLabel>
        </SloCheckList>
        <div class="add-to-project-errors" v-if="filteredErrors.length > 0">
          Test
          <ul>
            <li
              class="add-to-project-error-section"
              v-for="section of filteredErrors"
              :key="section.id"
            >
              <span>{{ section.name }}</span>
              <ul>
                <li v-for="error of section.errors" :key="error.id">
                  {{ error.message }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="buttons">
          <SloButton :disabled="submitDisabled" @click="add">
            {{ 'Add' | t }}
          </SloButton>
        </div>
      </section>
    </div>
  </SloModal>
</template>

<script>
import {mapActions, mapState, mapGetters} from 'vuex';
import t from '../../../main/i18n';
import {designspec} from '../../apis/designspec';
import SloModal from '../../../../lib/components/SloModal.vue';
import SloButton from '../../../../lib/components/SloButton.vue';
import SloField from '../../../../lib/components/SloField.vue';
import SloLabel from '../../../../lib/components/SloLabel.vue';
import SloInput from '../../../../lib/components/SloInput.vue';
import SloCheckList from '../../../../lib/components/SloCheckList.vue';
import SloSelect from '../../../../lib/components/SloSelect.vue';

export default {
  components: {
    SloModal,
    SloButton,
    SloField,
    SloLabel,
    SloInput,
    SloCheckList,
    SloSelect
  },
  props: {
    global: {type: Boolean},
    params: {type: Object}
  },
  data() {
    return {
      loading: false,
      errorsBySection: [],
      project: null,
      addToSections: []
    };
  },
  filters: {
    t
  },
  async created() {
    if (this.modalParams.project) {
      this.project = this.modalParams.project;
    } else if (this.projects.length === 1) {
      this.project = this.projects[0].id;
      if (Array.isArray(this.sections)) {
        const firstEnabled = this.sections.find(
          section =>
            section.products.length < 10 &&
            section.products.findIndex(
              p => p.type === this.type && `${p.id}` === `${this.id}`
            ) === -1
        );
        if (firstEnabled) {
          this.addToSections = [firstEnabled.id];
        }
      }
    }

    await this.validateProduct();
  },
  async mounted() {
    await this.getProjects();
  },
  watch: {
    async project() {
      if (this.sections && this.sections.length > 0) {
        const firstEnabled = this.checklistItems.find(item => !item.disabled);
        if (firstEnabled) {
          this.addToSections = [firstEnabled.value];
        }
      } else {
        this.addToSections = [];
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapState('designspec', ['projects']),
    ...mapGetters('designspec', {getProject: 'project', getSections: 'sections'}),
    filteredErrors() {
      return this.errorsBySection.filter(v => this.addToSections.indexOf(v.id) > -1);
    },
    checklistItems() {
      return this.sections.map(({id: value, name: label, products}) => {
        const maxProducts = products.length >= 10;
        const productExists =
          products.findIndex(
            p => p.type === this.type && `${p.id}` === `${this.id}`
          ) > -1;
        const disabled = maxProducts || productExists;
        let reason = null;
        if (disabled) {
          if (productExists) {
            reason = t('Product already added');
          }
          if (maxProducts) {
            reason = t('Section is full (10 products)');
          }
        }
        return {
          value,
          label,
          disabled,
          reason
        };
      });
    },
    submitDisabled() {
      return (this.projects.length > 1 && !this.project) ||
        (this.sections && this.sections.length > 0 && this.addToSections.length === 0) ||
        this.filteredErrors.length > 0;
    },
    modalParams() {
      return this.global ? this.params : this.$route.params;
    },
    sections() {
      if (typeof this.project === 'undefined') return [];
      try {
        return this.getSections(this.project);
      } catch (e) {
        return [];
      }
    },
    thumbnail() {
      return this.modalParams.thumbnail;
    },
    type() {
      return this.modalParams.type;
    },
    id() {
      return this.modalParams.id;
    }
  },
  methods: {
    ...mapActions('designspec', ['getProjects', 'addProducts']),
    async validateProduct() {
      if (!this.project) return;
      if (this.authenticated) {
        let messages = [];
        messages = await designspec.validateProduct(this.project, {
          type: this.type,
          id: this.id
        });
        if (messages.length === 0) {
          this.errorsBySection = [];
        }

        let errorsBySection = [];
        for (const section of this.sections || []) {
          const sectionErrors = messages.filter(
            ({type, id}) =>
              section.products.findIndex(p => p.type === type && p.id === id) >
              -1
          );

          if (sectionErrors.length > 0) {
            errorsBySection.push({
              id: section.id,
              name: section.name,
              errors: sectionErrors
            });
          }
        }
        this.errorsBySection = errorsBySection;
      }
    },
    toggleSection(id, checked) {
      if (!checked) {
        this.addToSections = [...this.addToSections, id];
      } else {
        this.addToSections = [...this.addToSections.filter(sid => sid !== id)];
      }
    },
    async add() {
      this.loading = true;
      const project = this.getProject(this.project);
      await this.addProducts({
        project: this.project,
        sections: this.addToSections,
        products: [{type: this.type, id: this.id}]
      });
      this.modalParams.onAdd &&
      this.modalParams.onAdd({
        name: project.name,
        thumbnail: this.thumbnail,
        url: `/designspec/project/${project.id}`
      });
      this.$modal.hide();
    },
    handleProjectSelection(value) {
      this.project = parseInt(value);
    }
  }
};
</script>

<style lang="scss">
@import '../../../../scss/system';

.add-to-project {
  min-height: 480px;

  section {
    flex: 1 0 50%;
    display: flex;
    flex-direction: column;

    &:last-of-type {
      margin-left: 10px;
    }
  }

  .product-thumbnail {
    max-height: 380px;

    .field--type-image {
      position: relative;
      width: 100%;
      height: auto;
      padding-bottom: 100%;

      img {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }

  .content {
    display: flex;
    flex-flow: row nowrap;

    @supports (display: grid) {
      display: grid;
      gap: 16px;

      @media (min-width: 675px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }

  figure {
    background-color: $color-gray-100;

    img {
      object-fit: contain;
    }
  }

  figure,
  .field--type-image,
  img {
    max-width: 100%;
    width: 100%;
    height: 100%;
  }

  .buttons {
    margin-top: auto !important;
    display: flex;
    align-items: center;

    .slo-button {
      margin-left: auto;
    }

    .slo-field {
      margin-left: 8px;
    }
  }

  .add-to-project-errors {
    h4 {
      @include text('sm', 'strong');
      color: $color-gray-700;
    }

    ul,
    li {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .add-to-project-error-section {
      @include text('sm', 'strong');
      background: rgba($color-red-400, 0.2);
      color: $color-red-400;
      border-radius: 4px;
      padding: 16px;
      justify-content: center;
    }

    li > ul {
      margin-left: 0;
      font-weight: normal;

      li + li {
        margin-top: 4px;
      }
    }
  }
}
</style>
